var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useUncontrolledProp } from 'uncontrollable';
import { PaginationSlider } from '@snack-uikit/pagination';
import { extractSupportProps } from '@snack-uikit/utils';
import { TEST_IDS } from '../../testIds';
import { Control } from '../Control';
import { ItemProvider } from '../ItemProvider';
import styles from './styles.module.css';
export function Carousel(_a) {
    var _b;
    var { children: items, showItems = 1, scrollBy: scrollByProp, transition = 0.4, swipe = true, arrows = true, pagination = true, className, gap, state, infiniteScroll = false, swipeActivateLength = 48, autoSwipe } = _a, rest = __rest(_a, ["children", "showItems", "scrollBy", "transition", "swipe", "arrows", "pagination", "className", "gap", "state", "infiniteScroll", "swipeActivateLength", "autoSwipe"]);
    const timerRef = useRef();
    const scrollBy = useMemo(() => scrollByProp !== null && scrollByProp !== void 0 ? scrollByProp : Math.trunc(showItems), [showItems, scrollByProp]);
    const [page, setPage] = useUncontrolledProp(state === null || state === void 0 ? void 0 : state.page, (_b = state === null || state === void 0 ? void 0 : state.page) !== null && _b !== void 0 ? _b : 0, newPage => {
        const result = typeof newPage === 'function' ? newPage(page) : newPage;
        state === null || state === void 0 ? void 0 : state.onChange(result);
    });
    const total = useMemo(() => {
        if (items.length <= showItems) {
            return 1;
        }
        return 1 + Math.ceil((items.length - showItems) / scrollBy);
    }, [items.length, scrollBy, showItems]);
    const onLeftArrowClick = useCallback(() => {
        setPage((page) => (infiniteScroll ? (total + page - 1) % total : Math.max(0, page - 1)));
    }, [infiniteScroll, setPage, total]);
    const onRightArrowClick = useCallback(() => {
        setPage((page) => (infiniteScroll ? (page + 1) % total : Math.min(total - 1, page + 1)));
    }, [infiniteScroll, setPage, total]);
    const slideCallback = useCallback((direction) => {
        if (direction < 0) {
            onRightArrowClick();
            return;
        }
        onLeftArrowClick();
    }, [onLeftArrowClick, onRightArrowClick]);
    useEffect(() => {
        if (!autoSwipe || !infiniteScroll) {
            return;
        }
        timerRef.current = setTimeout(() => onRightArrowClick(), autoSwipe * 1000);
        return () => clearTimeout(timerRef.current);
    }, [autoSwipe, infiniteScroll, onRightArrowClick]);
    return (_jsxs("div", Object.assign({ className: cn(styles.carousel, className) }, extractSupportProps(rest), { children: [_jsxs("div", { className: styles.carouselBase, children: [_jsx(ItemProvider, { showItems: showItems, scrollBy: scrollBy, swipe: swipe, transition: transition, items: items, slideCallback: slideCallback, page: page, gap: gap, swipeActivateLength: swipeActivateLength }), arrows && (_jsxs(_Fragment, { children: [(infiniteScroll || page > 0) && (_jsx(Control, { onClick: onLeftArrowClick, variant: 'prev', "data-test-id": TEST_IDS.arrowPrev })), (infiniteScroll || page + 1 < total) && (_jsx(Control, { onClick: onRightArrowClick, variant: 'next', "data-test-id": TEST_IDS.arrowNext }))] }))] }), pagination && (_jsx("div", { className: styles.pagination, children: _jsx(PaginationSlider, { "data-test-id": TEST_IDS.pagination, page: page + 1, onChange: (page) => {
                        setPage(page - 1);
                    }, total: total }) }))] })));
}
